import React, { useState, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import * as moment from "moment";
import classnames from "classnames";

const PaymentsListCard = ({
  formik,
  addPayment,
  handlePaymentTypeChange,
  handlePaymentDateChange,
  handleUnitAmountChange,
  getPaymentTotal,
  deletePayment,
  payments,
  settledPrice,
  handlePaymentStatusChange,
}) => {
  const [activeTab, setActiveTab] = useState("0");

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };
  

  const handleDeletePayment = useCallback(
    (index, e) => {
      e.stopPropagation();
      e.preventDefault();

      if (index.toString() <= activeTab && parseInt(activeTab) > 0) {
        setActiveTab((parseInt(activeTab) - 1).toString());
      }

      setTimeout(() => {
        deletePayment(index);
      }, 10);
    },
    [activeTab, deletePayment]
  );

  const addPaymentAndSwitchTab = () => {
    addPayment();

    setTimeout(() => {
      const newTabIndex = payments.length.toString();
      setActiveTab(newTabIndex);
    }, 50);
  };

  React.useEffect(() => {
    if (payments.length > 0 && parseInt(activeTab) >= payments.length) {
      setActiveTab((payments.length - 1).toString());
    }
  }, [payments.length, activeTab]);

  const EmptyPaymentsList = () => (
    <div className="text-center p-5">
      <div className="text-primary mb-3 opacity-75">
        <i className="ri-coin-line fs-1"></i>
      </div>
      <h5 className="fw-bold text-secondary mb-3">No Payments Added</h5>
      <p className="text-muted">
        Please add a payment to continue with the reservation process.
      </p>
      <Button color="primary" className="mt-2" onClick={addPayment}>
        <i className="ri-add-line me-1"></i>
        Add First Payment
      </Button>
    </div>
  );

  const getPaymentTypeColor = (type) => {
    const colors = {
      DOWN_PAYMENT: "info",
      PAYMENT: "primary",
      FULL_PAYMENT: "success",
      BALANCE: "warning",
      EXTRA: "danger",
      OTHER: "secondary",
    };
    return colors[type] || "light";
  };

  const getPaymentLabel = (type) => {
    const labels = {
      DOWN_PAYMENT: "Down Payment",
      PAYMENT: "Payment",
      FULL_PAYMENT: "Full Payment",
      BALANCE: "Balance",
      EXTRA: "Extra",
      OTHER: "Other",
    };
    return labels[type] || "Payment";
  };

  return (
    <Card>
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">
          Payments
          <Badge
            color={payments?.length ? "primary" : "light"}
            className="ms-2"
            pill
          >
            {payments?.length || 0}
          </Badge>
        </h5>
      </CardHeader>
      <CardBody>
        {!payments || payments.length === 0 ? (
          <EmptyPaymentsList />
        ) : (
          <>
            <div className="d-flex align-items-center mb-3">
              <Nav tabs className="nav-tabs mb-0 flex-grow-1">
                {payments.map((payment, index) => {
                  const hasErrors =
                    formik.errors.payments &&
                    formik.errors.payments[index] &&
                    (formik.errors.payments[index].type ||
                      formik.errors.payments[index].booking_date ||
                      (formik.errors.payments[index].unitAmounts &&
                        formik.errors.payments[index].unitAmounts.some(
                          (u) => u
                        )));
                  const showError =
                    hasErrors &&
                    formik.touched.payments &&
                    formik.touched.payments[index];

                  return (
                    <NavItem key={`tab-${index}`}>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: activeTab === index.toString(),
                          "border-danger":
                            showError && activeTab !== index.toString(),
                        })}
                        onClick={() => {
                          toggle(index.toString());
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <Badge
                            color={
                              showError
                                ? "danger"
                                : payment.type
                                ? getPaymentTypeColor(payment.type)
                                : "light"
                            }
                            className="me-1"
                            pill
                          >
                            {index + 1}
                            {showError && (
                              <i className="ri-error-warning-line ms-1 small"></i>
                            )}
                          </Badge>
                          <span className={showError ? "text-danger" : ""}>
                            {payment.type
                              ? getPaymentLabel(payment.type)
                              : "Payment"}
                          </span>
                          {showError && (
                            <i className="ri-error-warning-line ms-1 text-danger"></i>
                          )}
                          <Button
                            type="button"
                            color="link"
                            className="p-0 ms-2 text-danger btn-sm"
                            onClick={(e) => handleDeletePayment(index, e)}
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </div>
                      </NavLink>
                    </NavItem>
                  );
                })}
              </Nav>
              <Button
                type="button"
                color="primary"
                className="ms-2"
                size="sm"
                onClick={addPaymentAndSwitchTab}
              >
                <i className="ri-add-line me-1"></i>
                Add
              </Button>
            </div>

            <TabContent activeTab={activeTab} className="text-muted">
              {payments.map((payment, paymentIndex) => (
                <TabPane
                  tabId={paymentIndex.toString()}
                  key={`content-${paymentIndex}`}
                >
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">Payment Type</label>
                        <Input
                          type="select"
                          value={payment.type || ""}
                          onChange={(e) => {
                            handlePaymentTypeChange(
                              paymentIndex,
                              e.target.value
                            );
                          }}
                          className={
                            formik.errors.payments &&
                            formik.errors.payments[paymentIndex] &&
                            formik.errors.payments[paymentIndex].type
                              ? "is-invalid"
                              : ""
                          }
                          disabled={payment.isExtraPayment}
                        >
                          <option value="">Select Type</option>
                          <option value="DOWN_PAYMENT">Down Payment</option>
                          <option value="PAYMENT">Payment</option>
                          <option value="FULL_PAYMENT">Full Payment</option>
                          <option value="BALANCE">Balance</option>
                          <option value="EXTRA">Extra</option>
                          <option value="OTHER">Other</option>
                        </Input>
                        {formik.errors.payments &&
                          formik.errors.payments[paymentIndex] &&
                          formik.errors.payments[paymentIndex].type && (
                            <div className="invalid-feedback">
                              {formik.errors.payments[paymentIndex].type}
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">Payment Date</label>
                        <Flatpickr
                          className={
                            formik.errors.payments &&
                            formik.errors.payments[paymentIndex] &&
                            formik.errors.payments[paymentIndex].booking_date
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Payment Date"
                          options={{
                            enableTime: false,
                            altInput: true,
                            altFormat: "d.m.Y",
                            dateFormat: "Y-m-d",
                            allowInput: true,
                            disableMobile: true,
                          }}
                          onChange={(date) => {
                            if (date && date.length > 0) {
                              const formattedDate = moment(date[0]).isValid()
                                ? moment(date[0]).format("YYYY-MM-DD")
                                : null;
                              handlePaymentDateChange(
                                paymentIndex,
                                formattedDate
                              );
                            } else {
                              handlePaymentDateChange(paymentIndex, null);
                            }
                          }}
                          value={payment.booking_date || ""}
                        />
                        {formik.errors.payments &&
                          formik.errors.payments[paymentIndex] &&
                          formik.errors.payments[paymentIndex].booking_date && (
                            <div className="invalid-feedback">
                              {
                                formik.errors.payments[paymentIndex]
                                  .booking_date
                              }
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">Total Amount</label>
                        <div className="d-flex align-items-center">
                          <h4 className="mb-0">
                            <Badge color={"success"} className="px-3 py-2">
                              €
                              {Intl.NumberFormat("en-US").format(
                                getPaymentTotal(payment)
                              )}
                            </Badge>
                          </h4>

                          {settledPrice && (
                            <div className="d-flex align-items-center ms-3">
                              <div
                                className="progress mx-1"
                                style={{
                                  height: "6px",
                                  width: "80px",
                                }}
                              >
                                <div
                                  className="progress-bar bg-success"
                                  style={{
                                    width: `${Math.min(
                                      100,
                                      (getPaymentTotal(payment) /
                                        parseInt(settledPrice)) *
                                        100
                                    )}%`,
                                  }}
                                ></div>
                              </div>
                              <small className="text-muted ms-1">
                                {(
                                  (getPaymentTotal(payment) /
                                    parseInt(settledPrice)) *
                                  100
                                ).toFixed(1)}
                                %
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="mb-3">
                        <label className="form-label">Payment Status</label>
                        <div className="d-flex align-items-center">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`paymentStatus-${paymentIndex}`}
                              checked={payment.isPaid || false}
                              onChange={(e) => {
                                handlePaymentStatusChange(
                                  paymentIndex,
                                  e.target.checked
                                );
                              }}
                            />
                            <label
                              className="form-check-label"
                              htmlFor={`paymentStatus-${paymentIndex}`}
                            >
                              <Badge
                                color={payment.isPaid ? "success" : "warning"}
                                className="ms-2"
                              >
                                {payment.isPaid ? "Paid" : "Pending"}
                              </Badge>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="unit-amounts mt-3">
                    <div className="d-flex px-2 py-2 border-bottom">
                      <div
                        style={{ width: "15%" }}
                        className="text-muted small"
                      >
                        Unit
                      </div>
                      <div
                        style={{ width: "25%" }}
                        className="text-muted small"
                      >
                        Type
                      </div>
                      <div
                        style={{ width: "35%" }}
                        className="text-muted small"
                      >
                        Amount
                      </div>
                      <div
                        style={{ width: "25%" }}
                        className="text-muted small"
                      >
                        Remaining
                      </div>
                      <div
                        style={{ width: "10%" }}
                        className="text-muted small"
                      >
                        Percentage
                      </div>
                    </div>

                    <div className="units-list">
                      {(payment.unitAmounts || []).map(
                        (unitAmount, unitIndex) => (
                          <div
                            key={unitIndex}
                            className="d-flex align-items-center py-2 border-bottom"
                          >
                            <div style={{ width: "15%" }}>
                              <span className="badge bg-light text-dark">
                                {unitAmount.unit_nr}
                              </span>
                            </div>

                            <div style={{ width: "25%" }}>
                              {unitAmount.unit_type}
                            </div>
                            <div style={{ width: "35%" }}>
                              <div
                                className="input-group"
                                style={{ maxWidth: "150px" }}
                              >
                                <span className="input-group-text border-end-0">
                                  €
                                </span>
                                <input
                                  type="text"
                                  className={`form-control border-start-0 ${
                                    payment.type === "EXTRA" ? "bg-light" : ""
                                  } ${
                                    formik.errors.payments &&
                                    formik.errors.payments[paymentIndex]
                                      ?.unitAmounts?.[unitIndex]?.amount
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={
                                    unitAmount.amount
                                      ? Intl.NumberFormat("en-US").format(
                                          unitAmount.amount
                                        )
                                      : ""
                                  }
                                  onChange={(e) =>
                                    handleUnitAmountChange(
                                      paymentIndex,
                                      unitIndex,
                                      e.target.value
                                    )
                                  }
                                  placeholder="0"
                                />
                              </div>
                              {payment.type === "EXTRA" && (
                                <small className="text-muted">
                                  Auto-filled from extras
                                </small>
                              )}
                            </div>
                            <div style={{ width: "25%" }}>
                              <div className="d-flex flex-column">
                                  <span className={`small fw-medium ${unitAmount.remaining === 0 ? 'text-success' : 'text-primary'}`}>
                                    €{Intl.NumberFormat("en-US").format(unitAmount.remaining)}
                                  </span>
                                  <small className="text-muted">
                                    Settled Price: €{Intl.NumberFormat("en-US").format(unitAmount.settled_price || 0)}
                                  </small>
                                </div>
                            </div>
                            <div style={{ width: "10%" }}>
                              {unitAmount.amount && settledPrice ? (
                                <div className="d-flex align-items-center">
                                  <div
                                    className="progress me-2"
                                    style={{ height: "4px", width: "60px" }}
                                  >
                                    <div
                                      className="progress-bar bg-success"
                                      style={{
                                        width: `${Math.min(
                                          100,
                                          (parseInt(unitAmount.amount) /
                                            parseInt(settledPrice)) *
                                            100
                                        )}%`,
                                      }}
                                    ></div>
                                  </div>
                                  <span className="text-body">
                                    {(
                                      (parseInt(unitAmount.amount) /
                                        parseInt(settledPrice)) *
                                      100
                                    ).toFixed(1)}
                                    %
                                  </span>
                                </div>
                              ) : (
                                <span className="text-muted">0%</span>
                              )}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </TabPane>
              ))}
            </TabContent>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default PaymentsListCard;
